import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { GoMakeTreeSelect } from "@/components/auto-complete/tree-select";
import { useGomakeAxios } from "@/hooks";
import { ETab } from "@/pages-components/admin/home/widgets/quote-widget/enums";
import { ProductCategoryType } from "@/pages-components/admin/home/widgets/quote-widget/states";
import { getAllProductAndMaterialsApi } from "@/services/api-service/products/product-endpoints";

function collectCheckedMaterialLeafIds(
  checkedValues: string[],
  roots: any[],
): string[] {
  if (!checkedValues?.length || !roots?.length) return [];
  const checked = new Set(checkedValues.map(String));
  const out: string[] = [];

  const walk = (node: any) => {
    const value = String(node.value);
    if (!checked.has(value)) {
      node.children?.forEach(walk);
      return;
    }
    if (!node.children?.length) {
      if (node.level === 3 && node.category === ProductCategoryType.MATERIAL) {
        out.push(value);
      }
      return;
    }
    const addAllMaterialLeaves = (n: any) => {
      if (!n.children?.length) {
        if (n.level === 3 && n.category === ProductCategoryType.MATERIAL) {
          out.push(String(n.value));
        }
      } else {
        n.children.forEach(addAllMaterialLeaves);
      }
    };
    node.children.forEach(addAllMaterialLeaves);
  };

  roots.forEach(walk);
  return Array.from(new Set(out));
}

const PurchaseOrderMaterialFilter = ({
  printHouseMaterialIds,
  setPrintHouseMaterialIds,
}: {
  printHouseMaterialIds: string[];
  setPrintHouseMaterialIds: (ids: string[]) => void;
}) => {
  const { t } = useTranslation();
  const { callApi } = useGomakeAxios();
  const [materialRoots, setMaterialRoots] = useState<any[]>([]);

  const loadMaterials = useCallback(async () => {
    const callBack = (res) => {
      if (!res?.success) return;
      const materials = res?.data?.materials || [];
      setMaterialRoots(
        materials.map((material) => ({
          value: material.materialTypeName,
          title: material.materialTypeName,
          text: material.materialTypeName,
          level: 1,
          selectable: false,
          category: ProductCategoryType.MATERIAL,
          children: (material.categories || []).map((category) => ({
            value: `${material.materialTypeName}-${category.categoryName}`,
            title: category.categoryName,
            text: category.categoryName,
            selectable: false,
            level: 2,
            category: ProductCategoryType.MATERIAL,
            children: (category.rows || []).map((row) => ({
              value: row.id,
              title: row.value,
              text: row.value,
              level: 3,
              category: ProductCategoryType.MATERIAL,
            })),
          })),
        })),
      );
    };

    await getAllProductAndMaterialsApi(callApi, callBack, {
      selectionType: ETab.MATERIALS,
    });
  }, [callApi]);

  useEffect(() => {
    loadMaterials();
  }, [loadMaterials]);

  const options = useMemo(
    () => [
      {
        value: ETab.MATERIALS,
        title: t("tabs.materials"),
        text: t("tabs.materials"),
        level: 0,
        selectable: false,
        children: materialRoots,
      },
    ],
    [materialRoots, t],
  );

  return (
    <GoMakeTreeSelect
      selectedTab={ETab.MATERIALS}
      setSelectedTab={() => {}}
      options={options}
      placeholder={t("modal.pleaseSelectMaterial")}
      hideOtherTabs
      hideDropdownTabs
      wideDropdown
      materialMultiSelect
      multiValue={printHouseMaterialIds}
      onMultiChange={(values) => {
        setPrintHouseMaterialIds(
          collectCheckedMaterialLeafIds(values, materialRoots),
        );
      }}
      style={{ width: "100%" }}
    />
  );
};

export { PurchaseOrderMaterialFilter };
